//import {mungeSDPPublish} from './mungeSDP';

interface PublishSettings {
  signalingURL: string;
  applicationName: string;
  streamName: string;
  videoTrack?: MediaStreamTrack;
  audioTrack?: MediaStreamTrack;
  videoBitrate?: number;
  audioBitrate?: number;
  videoFrameRate?: number;
  videoCodec?: string;
  audioCodec?: string;
  userData?: any;
}

interface Callbacks {
  onError?: (error: {message: string}) => void;
  onConnectionStateChange?: (state: {connected: boolean}) => void;
  onSetPeerConnection?: (data: {peerConnection: RTCPeerConnection | undefined}) => void;
  onSetWebsocket?: (data: {websocket: WebSocket | undefined}) => void;
  onSetSenders?: (data: {audioSender?: RTCRtpSender; videoSender?: RTCRtpSender}) => void;
}

const getStreamInfo = (publishSettings: PublishSettings) => ({
  applicationName: publishSettings.applicationName,
  streamName: publishSettings.streamName,
  sessionId: publishSettings.streamName,
});

const getUserData = (publishSettings: PublishSettings) =>
  publishSettings.userData ? publishSettings.userData : {};

const peerConnectionCreateOfferSuccess = (
  description: RTCSessionDescriptionInit,
  publishSettings: PublishSettings,
  websocket: WebSocket,
  peerConnection: RTCPeerConnection,
  callbacks: Callbacks
) => {
  // Optional SDP munging if needed
  // description.sdp = mungeSDPPublish(description.sdp || '', {
  //   videoBitrate: publishSettings.videoBitrate ?? 3500,
  //   audioBitrate: publishSettings.audioBitrate ?? 64,
  //   videoCodec: publishSettings.videoCodec ?? '42e01f',
  //   audioCodec: publishSettings.audioCodec ?? 'opus',
  // });

  peerConnection
    .setLocalDescription(description)
    .then(() => {
      console.log('Replay: Sending publish open over socket');
      websocket.send(
        JSON.stringify({
          direction: 'publish',
          command: 'sendOffer',
          streamInfo: getStreamInfo(publishSettings),
          sdp: description,
          userData: getUserData(publishSettings),
        })
      );
    })
    .catch((error) => {
      console.error('Replay: Error setting local description or on sending stream info:', error);
      peerConnectionOnError(error, callbacks);
    });
};

const peerConnectionOnError = (error: any, callbacks: Callbacks) => {
  console.error('Replay: PeerConnection error', error);
  if (callbacks.onError) callbacks.onError({message: 'PeerConnection Error: ' + error.message});
};

const websocketOnOpen = (
  publishSettings: PublishSettings,
  websocket: WebSocket,
  callbacks: Callbacks
) => {
  let peerConnection: RTCPeerConnection | undefined;

  try {
    peerConnection = new RTCPeerConnection();

    peerConnection.onnegotiationneeded = () => {
      if (peerConnection) {
        console.log('Replay: Creating offer....');
        peerConnection
          .createOffer()
          .then((description) => {
            peerConnectionCreateOfferSuccess(
              description,
              publishSettings,
              websocket,
              peerConnection!,
              callbacks
            );
            console.log('Replay: offer successful');
          })
          .catch((e) => {
            console.log('Replay: offer failed', e);
            peerConnectionOnError(e, callbacks);
          });
      }
    };

    peerConnection.onconnectionstatechange = (event) => {
      console.log('Replay: Connection state changed:', event);
      const pcState = event.target as RTCPeerConnection;
      if (pcState.connectionState === 'connected') {
        callbacks.onConnectionStateChange?.({connected: true});
      } else {
        callbacks.onConnectionStateChange?.({connected: false});
      }
    };

    let audioSender: RTCRtpSender | undefined;
    let videoSender: RTCRtpSender | undefined;
    if (publishSettings.audioTrack) {
      audioSender = peerConnection.addTrack(publishSettings.audioTrack);
    }
    if (publishSettings.videoTrack) {
      videoSender = peerConnection.addTrack(publishSettings.videoTrack);

      // Set encoding parameters for the sender
      const parameters = videoSender.getParameters();
      if (!parameters.encodings) {
        parameters.encodings = [{}]; // Ensure encodings array is initialized
      }

      // Update the encoding parameters
      if (parameters.encodings[0].maxBitrate) {
        parameters.encodings[0].maxBitrate = 256000; // Set target max bit rate (e.g., 3 Mbps)
      }
      if (parameters.encodings[0].maxFramerate) {
        parameters.encodings[0].maxFramerate = 24; // Set max frame rate (optional)
      }
      if (parameters.encodings[0].scaleResolutionDownBy) {
        parameters.encodings[0].scaleResolutionDownBy = 1.0; // No downscaling
      }
      if (parameters.encodings[0].networkPriority) {
        parameters.encodings[0].networkPriority = 'high'; // Optional: Set priority (e.g., "high", "medium", "low")
      }

      videoSender
        .setParameters(parameters)
        .then(() => {})
        .catch((error) => {
          console.log('Replay: Failed to update encoding parameters:', error);
        });
    }
    callbacks.onSetSenders?.({audioSender, videoSender});

    websocket.addEventListener('message', (event) => {
      websocketOnMessage(event, publishSettings, peerConnection!, callbacks);
    });

    callbacks.onSetPeerConnection?.({peerConnection});
  } catch (e) {
    websocketOnError(e, callbacks);
  }
};

const websocketOnMessage = (
  event: MessageEvent,
  publishSettings: PublishSettings,
  peerConnection: RTCPeerConnection,
  callbacks: Callbacks
) => {
  console.log('Replay: WebSocket message:', event.data);
  const msgJSON = JSON.parse(event.data);
  const msgStatus = Number(msgJSON['status']);

  if (msgStatus === 504) {
    console.error('Replay: WebSocket timeout:', msgJSON['statusDescription']);
  } else if (msgStatus !== 200) {
    websocketOnError({message: msgJSON['statusDescription']}, callbacks);
  } else {
    const sdpData = msgJSON['sdp'];
    if (sdpData) {
      peerConnection
        .setRemoteDescription(new RTCSessionDescription(sdpData))
        .then(() => {})
        .catch((error) => peerConnectionOnError(error, callbacks));
    }

    const iceCandidates = msgJSON['iceCandidates'];
    if (iceCandidates) {
      for (const candidate of iceCandidates) {
        console.log('Replay: Adding ICE candidate:', candidate);
        peerConnection
          .addIceCandidate(new RTCIceCandidate(candidate))
          .catch((error) => console.error('Replay: Error adding ICE candidate:', error));
      }
    }
  }
};

const websocketOnError = (error: any, callbacks: Callbacks) => {
  console.error('Replay: WebSocket error:', error);
  if (callbacks.onError) callbacks.onError({message: 'WebSocket Error: ' + error.message});
};

const startPublish = (
  publishSettings: PublishSettings,
  websocket: WebSocket | null,
  callbacks: Callbacks
) => {
  try {
    console.log('Replay: Starting publish....', publishSettings);
    if (!websocket) {
      websocket = new WebSocket(publishSettings.signalingURL);
    }

    websocket.binaryType = 'arraybuffer';
    websocket.addEventListener('open', () =>
      websocketOnOpen(publishSettings, websocket!, callbacks)
    );
    websocket.addEventListener('error', (error) => websocketOnError(error, callbacks));

    callbacks.onSetWebsocket?.({websocket: websocket});
  } catch (e) {
    if (callbacks.onError) callbacks.onError(e);
  }
};

export default startPublish;
